import { useEffect, useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useAuth } from "../../auth.tsx";
import { api, useAction } from "../../api.ts";
import { plural } from "../../format.ts";
import { Button, ErrorText, Icon, Input, cn, useToast } from "../../ui/index.tsx";
import { Burst, EASE, Springy, useCalm } from "../member/kit.tsx";
import { formatKey } from "./model.ts";
import type { Entitlement, RedeemResult } from "../../../shared/types.ts";

const SCAN_STEPS = ["HANDSHAKE", "VERIFYING SIGNATURE", "BINDING LICENSE"];
const SEGMENTS = [0, 1, 2, 3, 4];

/** Five bars that fill as the prefix and each 5-character group are typed. */
function SegmentBar({ value }: { value: string }) {
  const parts = value.split("-");
  return (
    <div className="grid grid-cols-[0.7fr_1fr_1fr_1fr_1fr] gap-1.5" aria-hidden>
      {SEGMENTS.map((i) => {
        const len = parts[i]?.length ?? 0;
        const fill = i === 0 ? (parts[0] === "EXO" ? 1 : len / 3) : len / 5;
        return (
          <div key={i} className="h-1.5 overflow-hidden rounded-full bg-raised">
            <div className={cn("h-full rounded-full transition-[width,background-color] duration-200", fill >= 1 ? "bg-accent shadow-[0_0_10px_var(--color-accent)]" : "bg-accent/50")}
              style={{ width: `${fill * 100}%` }} />
          </div>
        );
      })}
    </div>
  );
}

export function Redeem({ onRedeemed }: { onRedeemed: () => Promise<void> }) {
  const { refresh } = useAuth();
  const toast = useToast();
  const calm = useCalm();
  const [key, setKey] = useState("");
  const [step, setStep] = useState(0);
  const [burst, setBurst] = useState(0);
  const [shake, setShake] = useState(0);
  const [granted, setGranted] = useState<Entitlement[] | null>(null);

  const redeem = useAction(async () => {
    setGranted(null);
    const r = await api.post<RedeemResult>("/me/redeem", { key: key.trim() });
    setKey("");
    toast(r.granted.length ? `Redeemed. ${plural(r.granted.length, "game")} updated.` : "Key redeemed", "success");
    setGranted(r.granted);
    setBurst((b) => b + 1);
    await Promise.all([refresh(), onRedeemed()]);
    return r;
  });

  // Cycle the status line while the request is in flight (a few state updates in total).
  useEffect(() => {
    if (!redeem.pending) { setStep(0); return; }
    const t = setInterval(() => setStep((s) => Math.min(s + 1, SCAN_STEPS.length - 1)), 650);
    return () => clearInterval(t);
  }, [redeem.pending]);

  useEffect(() => {
    if (!granted) return;
    const t = setTimeout(() => setGranted(null), 6000);
    return () => clearTimeout(t);
  }, [granted]);

  const submit = async () => { if ((await redeem.run()) === undefined) setShake((s) => s + 1); };
  const onSubmit = (e: FormEvent) => { e.preventDefault(); void submit(); };

  return (
    <section id="redeem" className="pane hud hud-accent relative scroll-mt-4 p-5">
      <div className="mb-4 flex items-start gap-3">
        <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-accent-soft text-accent"><Icon name="key" className="size-5" /></div>
        <div className="min-w-0">
          <h2 className="font-display text-lg font-semibold uppercase tracking-wide">Redeem a key</h2>
          <p className="text-xs text-muted">Paste it anywhere. We add the dashes.</p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <motion.div key={shake} className="relative overflow-hidden rounded-lg" animate={shake ? { x: [0, -9, 9, -6, 6, 0] } : undefined} transition={{ duration: 0.4 }}>
          <Input id="redeem-key" aria-label="License key" value={key} onChange={(e) => setKey(formatKey(e.target.value))}
            placeholder="EXO-XXXXX-XXXXX-XXXXX-XXXXX" disabled={redeem.pending} spellCheck={false} autoComplete="off" autoCapitalize="characters"
            className={cn("!h-12 text-center font-mono !text-[13px] tracking-[0.08em] sm:!text-sm sm:tracking-[0.12em]", redeem.pending && "border-accent/60 !opacity-100")} />
          <AnimatePresence>
            {redeem.pending && (
              <motion.div className="pointer-events-none absolute inset-0 bg-bg/70" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} aria-hidden>
                {!calm && (
                  <motion.div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-accent/35 to-transparent"
                    initial={{ x: "-100%" }} animate={{ x: "300%" }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
                )}
                <div className="absolute inset-0 flex items-center justify-center font-mono text-[11px] tracking-[0.2em] text-accent">
                  <motion.span key={step} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>{SCAN_STEPS[step]}</motion.span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <SegmentBar value={key} />
        <ErrorText>{redeem.error}</ErrorText>

        <Springy className="!block"><Button type="submit" variant="primary" size="lg" icon="key" className="w-full" loading={redeem.pending} disabled={key.trim().length < 8}>Redeem key</Button></Springy>
      </form>

      <Burst id={burst} />

      <AnimatePresence>
        {granted && (
          <motion.div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-ok/30 bg-ok/10 px-3 py-2.5 text-sm text-ok" role="status"
            initial={{ opacity: 0, height: 0, y: -6 }} animate={{ opacity: 1, height: "auto", y: 0 }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.35, ease: EASE }}>
            <motion.span initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 400, damping: 14 }}><Icon name="check" className="size-4" /></motion.span>
            <span className="font-mono text-[11px] uppercase tracking-[0.16em]">Access granted</span>
            {granted.map((e, i) => (
              <motion.span key={e.game_id} className="rounded-md border border-ok/30 bg-bg/40 px-2 py-0.5 text-xs text-fg"
                initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 18, delay: 0.15 + i * 0.07 }}>
                {e.game_name}
              </motion.span>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
